import { consoleRoutes } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Badge, buttonClasses, EmptyState, ErrorState, Field, Icon, Select, Skeleton } from "@sagot/ui";
import { useWorkspaceContext } from "../../../../session/workspace-context";
import type { ConnectorCallStep } from "../types";
import { useConnectorsPicker, useOperationsPicker, type OperationSignature, type PickerConnector } from "./use-connector-ops";

const C = "engage.workflows.connector.op";

interface PickerProps {
  step: ConnectorCallStep;
  disabled: boolean;
  /** Picking another connector or operation starts the step over (its parameters and outputs belonged to the old one). */
  onPick: (connectorId: string, operationKey: string) => void;
  signature: OperationSignature | undefined;
}

function PickerSkeleton({ label }: { label: string }) {
  return (
    <div role="status" aria-label={label} className="space-y-2" data-testid="op-picker-loading">
      <Skeleton className="h-4 w-24" />
      <Skeleton className="h-10 w-full" />
      <Skeleton className="h-4 w-24" />
      <Skeleton className="h-10 w-full" />
    </div>
  );
}

function OperationField({ step, disabled, onPick, signature }: PickerProps) {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  const operations = useOperationsPicker(step.connector_id);
  if (operations.isPending) return <PickerSkeleton label={t(`${C}.loadingOperations`)} />;
  if (operations.isError) return <ErrorState headingLevel={3} title={t(`${C}.operationsFailedTitle`)} description={t(`${C}.loadFailedBody`)} retryLabel={t(`${C}.retry`)} onRetry={() => void operations.refetch()} />;
  const list = operations.data;
  const chosen = list.find((operation) => operation.key === step.operation_key);
  if (list.length === 0) {
    return (
      <EmptyState
        icon="plug"
        headingLevel={3}
        title={t(`${C}.noOperationsTitle`)}
        description={t(`${C}.noOperationsBody`)}
        action={<AppLink className={buttonClasses({ variant: "secondary", size: "sm" })} href={consoleRoutes.settings(summary.slug, "connectors")}>{t(`${C}.openConnectors`)}</AppLink>}
      />
    );
  }
  const gone = step.operation_key !== "" && chosen === undefined;
  const unpublished = chosen?.published_version === null;
  const error = gone ? t(`${C}.operationGone`) : unpublished ? t(`${C}.operationUnpublished`) : undefined;
  return (
    <div>
      <Field label={t(`${C}.operation`)} hint={t(`${C}.operationHint`)} error={error}>
        <Select value={step.operation_key} disabled={disabled} onChange={(event) => { onPick(step.connector_id, event.target.value); }}>
          <option value="">{t(`${C}.chooseOperation`)}</option>
          {gone ? <option value={step.operation_key}>{t(`${C}.operationGoneOption`, { key: step.operation_key })}</option> : null}
          {list.map((operation) => (
            <option key={operation.key} value={operation.key} disabled={operation.published_version === null && operation.key !== step.operation_key}>
              {operation.published_version === null ? t(`${C}.unpublishedOption`, { name: operation.name }) : operation.name}
            </option>
          ))}
        </Select>
      </Field>
      {chosen && signature ? (
        <p className="mt-2 flex flex-wrap items-center gap-2 text-xs text-fg-muted" data-testid="op-picker-meta">
          <Badge tone={signature.kind === "read" ? "neutral" : "warning"}>{t(`${C}.kinds.${signature.kind}`, { defaultValue: signature.kind })}</Badge>
          <code className="font-mono">{signature.key}</code>
          {signature.publishedVersion ? <span>{t(`${C}.version`, { version: signature.publishedVersion })}</span> : null}
        </p>
      ) : null}
      {signature && signature.kind !== "read" ? (
        <p className="mt-2 flex items-start gap-2 rounded-md bg-warning-soft p-3 text-xs" role="note" data-testid="op-write-note">
          <Icon name="alert-triangle" className="mt-0.5 size-3.5 shrink-0" />
          {t(`${C}.writeNote`)}
        </p>
      ) : null}
    </div>
  );
}

function connectorLabel(connector: PickerConnector, t: (key: string, options?: Record<string, unknown>) => string): string {
  if (connector.status !== "active") return t(`${C}.inactiveOption`, { name: connector.name, status: t(`${C}.statuses.${connector.status}`, { defaultValue: connector.status }) });
  return connector.sandbox ? t(`${C}.sandboxOption`, { name: connector.name }) : connector.name;
}

/** The connector and the operation a step calls: the connectors of the workspace, then the operations of the chosen one. */
export function OperationPicker(props: PickerProps) {
  const { step, disabled, onPick } = props;
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  const { query, readable } = useConnectorsPicker();
  const connectorsPage = consoleRoutes.settings(summary.slug, "connectors");
  if (!readable) return <p className="rounded-md bg-surface-2 p-3 text-xs text-fg-muted" role="note" data-testid="op-no-access">{t(`${C}.noAccess`)}</p>;
  if (query.isPending) return <PickerSkeleton label={t(`${C}.loading`)} />;
  if (query.isError) return <ErrorState headingLevel={3} title={t(`${C}.loadFailedTitle`)} description={t(`${C}.loadFailedBody`)} retryLabel={t(`${C}.retry`)} onRetry={() => void query.refetch()} />;
  const connectors = query.data;
  if (connectors.length === 0 && step.connector_id === "") {
    return (
      <EmptyState
        icon="plug"
        headingLevel={3}
        title={t(`${C}.emptyTitle`)}
        description={t(`${C}.emptyBody`)}
        action={<AppLink className={buttonClasses({ variant: "secondary", size: "sm" })} href={connectorsPage}>{t(`${C}.add`)}</AppLink>}
      />
    );
  }
  const chosen = connectors.find((connector) => connector.id === step.connector_id);
  const gone = step.connector_id !== "" && chosen === undefined;
  const inactive = chosen !== undefined && chosen.status !== "active";
  const error = gone ? t(`${C}.connectorGone`) : inactive ? t(`${C}.connectorInactive`) : undefined;
  return (
    <div className="space-y-4" data-testid="op-picker">
      <div>
        <Field label={t(`${C}.connector`)} hint={t(`${C}.connectorHint`)} error={error}>
          <Select value={step.connector_id} disabled={disabled} onChange={(event) => { onPick(event.target.value, ""); }}>
            <option value="">{t(`${C}.chooseConnector`)}</option>
            {gone ? <option value={step.connector_id}>{t(`${C}.connectorGoneOption`)}</option> : null}
            {connectors.map((connector) => (
              <option key={connector.id} value={connector.id} disabled={connector.status !== "active" && connector.id !== step.connector_id}>
                {connectorLabel(connector, t)}
              </option>
            ))}
          </Select>
        </Field>
        <p className="mt-2 text-xs">
          <AppLink className="font-semibold text-primary-ink underline" href={connectorsPage}>{t(`${C}.openConnectors`)}</AppLink>
        </p>
      </div>
      {step.connector_id !== "" && !gone ? <OperationField {...props} /> : null}
    </div>
  );
}
